import { useQueryClient } from '@tanstack/react-query';
import { differenceInMilliseconds } from 'date-fns';
import debounce from 'debounce';
import { useEffect, useMemo, useRef } from 'react';
import { tabInactiveThresholdMs } from '../lib/config';
import { useDryRunStore } from './useDryRunWs';
import { useNetworkStore } from './useNetworkStore';
import { useWebsocketStore } from './useWebsocketStore';

const WAKE_CHECK_INTERVAL = 1000; // Check every second
const WAKE_THRESHOLD = 3000; // If more than 3 seconds have passed, we likely woke from sleep
const INVALIDATE_DEBOUNCE = 500; // Debounce multiple invalidation triggers

/**
 * Network and Wake Detection Hook
 *
 * Detects when the computer wakes from sleep, network changes, or tab visibility changes
 * and invalidates all queries to ensure fresh data.
 *
 * Why this is needed:
 * - Many queries use staleTime: Infinity and rely on WebSocket updates for invalidation
 * - During computer sleep, offline periods, or inactive tabs, WebSocket connections may be lost
 * - React Query's refetchOnWindowFocus doesn't help because it respects staleTime
 * - This ensures data consistency by force-refetching all queries regardless of staleTime
 *
 * State is stored in Zustand so any component can access network status.
 */
export const useNetworkAndWakeDetection = () => {
  const queryClient = useQueryClient();
  const reconnectSubscriptions = useWebsocketStore((state) => state.reconnect);
  const reconnectDryRunSocket = useDryRunStore((state) => state.reconnect);

  const { setOnline, setTabVisible, setWakeDetected } = useNetworkStore();
  const isInitialMount = useRef(true);

  // Create stable debounced invalidate function
  const debouncedInvalidate = useMemo(
    () =>
      debounce(() => {
        void queryClient.invalidateQueries({ type: 'all' });
        reconnectSubscriptions();
        reconnectDryRunSocket();
      }, INVALIDATE_DEBOUNCE),
    [queryClient, reconnectDryRunSocket, reconnectSubscriptions]
  );

  // Wake detection using timer gaps
  useEffect(() => {
    let lastCheck = Date.now();

    const checkForWake = () => {
      const now = Date.now();
      const timeSinceLastCheck = now - lastCheck;

      if (timeSinceLastCheck > WAKE_THRESHOLD) {
        console.log(`[Wake] Detected: ${timeSinceLastCheck}ms gap`);
        setWakeDetected();
        debouncedInvalidate();
      }

      lastCheck = now;
    };

    const interval = setInterval(checkForWake, WAKE_CHECK_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [debouncedInvalidate, setWakeDetected]);

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => {
      console.log('[Network] Back online');
      setOnline(true);

      // Only invalidate if not initial mount
      if (!isInitialMount.current) {
        debouncedInvalidate();
      }
    };

    const handleOffline = () => {
      console.log('[Network] Went offline');
      setOnline(false);

      void queryClient.cancelQueries();
    };

    // Set initial state
    setOnline(navigator.onLine);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [debouncedInvalidate, queryClient, setOnline]);

  // Tab visibility detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      console.log('[Tab] Visibility changed:', isVisible);
      setTabVisible(isVisible);

      if (isVisible && navigator.onLine && !isInitialMount.current) {
        const { lastBackgroundTime } = useNetworkStore.getState();
        if (lastBackgroundTime) {
          const inactiveDuration = differenceInMilliseconds(
            new Date(),
            new Date(lastBackgroundTime)
          );
          if (inactiveDuration > tabInactiveThresholdMs) {
            console.log(`[Tab] Was inactive for ${inactiveDuration}ms, invalidating queries`);
            debouncedInvalidate();
          }
        }
      }
    };

    setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Mark initial mount as complete after all effects run
    isInitialMount.current = false;

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [debouncedInvalidate, setTabVisible]);
};
